import React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import { PlanningTicket, PlanningTicketStore, Milestone, MilestoneStore } from '../../store/planning';
import { Project, ProjectStore } from '../../store/Project';
import { User, UserStore } from '../../store/User';
import { Content } from 're-cy-cle';
import TicketEditModal from './TicketEditModal';

const PALETTE = ['#e53e3e', '#38a169', '#3182ce', '#805ad5', '#dd6b20', '#319795', '#d53f8c', '#6b46c1'];

function projectColor(p: any): string {
    if (p?.color) return p.color;
    const name = p?.name || '';
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

function userColor(u: any): string {
    const name = (u?.fullName || u?.firstName || u?.email || '') as string;
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

const COLUMN_CONFIG = [
    { status: 'todo', label: 'To Do', color: '#808080' },
    { status: 'in_progress', label: 'In Progress', color: '#3182ce' },
    { status: 'review', label: 'Review', color: '#dd6b20' },
    { status: 'done', label: 'Done', color: '#38a169' },
];

@observer
export default class SprintBoard extends React.Component<any, any> {
    @observable selectedMilestoneId: number | null = null;
    @observable dragTicketId: number | null = null;
    @observable loading = false;
    @observable editModalOpen = false;
    @observable editingTicket: PlanningTicket | null = null;

    ticketStore = new PlanningTicketStore({ relations: ['milestone', 'assignee'] });
    milestoneStore = new MilestoneStore({ relations: ['project'] });
    projectStore = new ProjectStore({ relations: ['company'] });
    userStore = new UserStore();

    async componentDidMount() {
        await this.fetchAll();
    }

    @action async fetchAll() {
        this.loading = true;
        await Promise.all([
            this.milestoneStore.fetch().catch((e: any) => console.warn('milestone fetch', e)),
            this.ticketStore.fetch().catch((e: any) => console.warn('ticket fetch', e)),
            this.projectStore.fetch().catch((e: any) => console.warn('project fetch', e)),
            this.userStore.fetch().catch((e: any) => console.warn('user fetch', e)),
        ]);
        if (!this.selectedMilestoneId && this.milestones.length > 0) {
            this.selectedMilestoneId = this.milestones[0].id;
        }
        this.loading = false;
    }

    get milestones(): Milestone[] {
        return (this.milestoneStore.models as Milestone[]) || [];
    }

    get tickets(): PlanningTicket[] {
        return (this.ticketStore.models as PlanningTicket[]) || [];
    }

    get projects(): Project[] {
        return (this.projectStore.models as Project[] || []).filter(p => !p.hidden);
    }

    get users(): User[] {
        return (this.userStore.models as User[]) || [];
    }

    get selectedMilestone(): Milestone | null {
        if (!this.selectedMilestoneId) return null;
        return this.milestones.find(m => m.id === this.selectedMilestoneId) || null;
    }

    get boardTickets(): PlanningTicket[] {
        if (!this.selectedMilestoneId) return [];
        return this.tickets.filter((t: any) => {
            const msId = t.milestone?.id ?? t.milestone;
            return Number(msId) === Number(this.selectedMilestoneId);
        });
    }

    getProjectForTicket(ticket: any): Project | null {
        const ms = ticket.milestone;
        const projId = ms?.project?.id ?? ms?.project;
        if (!projId) return null;
        return this.projects.find(p => Number(p.id) === Number(projId)) || null;
    }

    getUserById(id: any): User | null {
        return this.users.find(u => Number(u.id) === Number(id)) || null;
    }

    @action selectMilestone = (id: number) => {
        this.selectedMilestoneId = id;
    };

    @action onDragStart = (ticketId: number) => {
        this.dragTicketId = ticketId;
    };

    @action onDrop = async (status: string) => {
        if (!this.dragTicketId) return;
        const ticketId = this.dragTicketId;
        this.dragTicketId = null;
        const ticket = this.tickets.find((t: any) => t.id === ticketId);
        if (!ticket || (ticket as any).status === status) return;
        try {
            const api = (this.ticketStore as any).api;
            await api.patch('planning_ticket/' + ticketId + '/', { status });
            await this.ticketStore.fetch();
        } catch (e: any) {
            console.error('SprintBoard status change error:', e);
        }
    };

    @action openEditModal = (ticket: PlanningTicket) => {
        this.editingTicket = ticket;
        this.editModalOpen = true;
    };

    @action closeEditModal = () => {
        this.editModalOpen = false;
        this.editingTicket = null;
    };

    @action afterTicketSave = async () => {
        this.closeEditModal();
        await this.ticketStore.fetch();
    };

    renderColumn(col: { status: string; label: string; color: string }): React.ReactNode {
        const colTickets = this.boardTickets.filter((t: any) => (t as any).status === col.status);

        return (
            <div
                key={col.status}
                data-test-kanban-column={col.status}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => this.onDrop(col.status)}
                style={{
                    flex: 1,
                    background: '#fff',
                    borderRadius: 8,
                    border: '1px solid #e2e5e9',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    minHeight: 400,
                }}
            >
                <div style={{
                    padding: '12px 16px',
                    fontWeight: 600,
                    fontSize: 13,
                    borderBottom: '1px solid #e2e5e9',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}>
                    <span style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: col.color, display: 'inline-block',
                    }} />
                    {col.label}
                    <span style={{
                        background: '#f0f2f5', borderRadius: 10,
                        padding: '1px 8px', fontSize: 11, color: '#6b7280',
                    }}>
                        {colTickets.length}
                    </span>
                </div>
                <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
                    {colTickets.map((ticket: any) => {
                        const proj = this.getProjectForTicket(ticket);
                        const assignee = ticket.assignee;
                        const user = assignee ? this.getUserById(assignee.id ?? assignee) : null;
                        return (
                            <div
                                key={ticket.id}
                                data-test-kanban-card={ticket.id}
                                draggable
                                onDragStart={() => this.onDragStart(ticket.id)}
                                onClick={() => this.openEditModal(ticket)}
                                style={{
                                    background: '#f8f9fa',
                                    border: '1px solid #e2e5e9',
                                    borderRadius: 8,
                                    padding: 10,
                                    marginBottom: 8,
                                    cursor: 'grab',
                                }}
                            >
                                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>
                                    {ticket.title || ('#' + ticket.id)}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {proj && (
                                        <span style={{
                                            width: 8, height: 8, borderRadius: '50%',
                                            background: projectColor(proj), display: 'inline-block',
                                        }} />
                                    )}
                                    {user ? (
                                        <React.Fragment>
                                            <span style={{
                                                background: userColor(user), width: 18, height: 18,
                                                borderRadius: '50%', fontSize: 8, color: '#fff',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                {((user as any).fullName || (user as any).firstName || '?').charAt(0)}
                                            </span>
                                            <span style={{ fontSize: 11, color: '#6b7280' }}>
                                                {((user as any).fullName || (user as any).firstName || '').split(' ')[0]}
                                            </span>
                                        </React.Fragment>
                                    ) : (
                                        <span style={{ fontSize: 11, color: '#9ca3af' }}>Unassigned</span>
                                    )}
                                    <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 'auto' }}>
                                        {ticket.estimatedHours || 0}h
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                    {colTickets.length === 0 && (
                        <div style={{ color: '#9ca3af', fontSize: 12, textAlign: 'center', padding: 20 }}>
                            No tickets
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        return (
            <Content>
                <div data-test-sprint-board style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '8px 16px', borderBottom: '1px solid #e2e5e9',
                        background: '#fff', flexShrink: 0, flexWrap: 'wrap',
                    }}>
                        <select
                            data-test-sprint-board-selector
                            value={this.selectedMilestoneId || ''}
                            onChange={(e) => this.selectMilestone(Number(e.target.value))}
                            style={{
                                padding: '6px 12px', border: '1px solid #e2e5e9',
                                borderRadius: 8, background: '#fff', fontSize: 14,
                            }}
                        >
                            {this.milestones.map((m: any) => (
                                <option key={m.id} value={m.id}>
                                    {m.name} ({m.status})
                                </option>
                            ))}
                        </select>
                        {this.selectedMilestone && (
                            <span style={{ fontSize: 13, color: '#6b7280' }}>
                                {(this.selectedMilestone as any).type || 'milestone'}
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 16, flex: 1, overflow: 'hidden', padding: 16 }}>
                        {COLUMN_CONFIG.map(col => this.renderColumn(col))}
                    </div>
                    {this.editModalOpen && this.editingTicket && (
                        <TicketEditModal
                            model={this.editingTicket}
                            open={this.editModalOpen}
                            onClose={this.closeEditModal}
                            afterSave={this.afterTicketSave}
                            trigger={<span style={{ display: 'none' }} />}
                        />
                    )}
                </div>
            </Content>
        );
    }
}
